/**
 * The blocks a message renders, each one once.
 *
 * A message can carry the SAME block twice: V4 wrote an `extends` entry per mutation and
 * the mirrored writes never deduped, so an accept that was retried (or written by both the
 * V4 and the channel path) leaves two rows that say exactly the same thing. The prompt
 * endpoint already collapses those — it answers with the first prompt block per message
 * (`channel-prompt-block.repository.ts`) — so a thread that renders every row shows a card
 * the prompt bar counted once, twice.
 *
 * Identity is (`type`, payload), NOT the row `id`: the duplicates are distinct rows. Two
 * blocks of the same type carrying different payloads are two different cards and both
 * survive, and the FIRST occurrence wins so the surviving order stays the insertion order
 * the loader sorts by.
 */

/** Key-sorted so `{visitId,timeProposalId}` and `{timeProposalId,visitId}` collapse. */
function stablePayloadKey(payload: unknown): string {
  if (payload === null || typeof payload !== "object") {
    return JSON.stringify(payload) ?? "undefined";
  }

  if (Array.isArray(payload)) {
    return `[${payload.map(stablePayloadKey).join(",")}]`;
  }

  const entries = Object.entries(payload as Record<string, unknown>)
    .filter(([, value]) => value !== undefined)
    .sort(([left], [right]) => left.localeCompare(right));

  return `{${entries
    .map(([key, value]) => `${JSON.stringify(key)}:${stablePayloadKey(value)}`)
    .join(",")}}`;
}

type BlockLike = { type: string; payload?: unknown };

export function dedupeMessageBlocks<T extends BlockLike>(
  blocks: readonly (T | null | undefined)[] | null | undefined,
): T[] {
  if (!blocks?.length) return [];

  const seen = new Set<string>();
  const deduped: T[] = [];

  for (const block of blocks) {
    if (!block) continue;
    const key = `${block.type}|${stablePayloadKey(block.payload)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(block);
  }

  return deduped;
}
